import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Check, Heart } from "lucide-react";
import { BlockMath } from "react-katex";
import api from "@/lib/api";
import Mashy from "@/components/Mashy";
import ProgressBar from "@/components/ProgressBar";

export default function Lesson() {
  const { id } = useParams();
  const nav = useNavigate();
  const [lesson, setLesson] = useState(null);
  const [picked, setPicked] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [textAns, setTextAns] = useState("");

  useEffect(() => {
    api.get(`/lessons/${id}`).then((r) => setLesson(r.data.lesson)).catch(() => {});
  }, [id]);

  if (!lesson) return <div className="min-h-screen flex items-center justify-center" data-testid="lesson-loading">Loading…</div>;

  const c = lesson.content || {};
  const isMCQ = lesson.lesson_type === "mcq";
  const isInput = lesson.lesson_type === "input";
  const isVoice = lesson.lesson_type === "voice";

  const correctAnswer = (c.answer || c.expected || "").toString().trim().toLowerCase();
  const isCorrect = submitted && (
    (isMCQ && picked === c.answer) ||
    (isInput && textAns.trim().toLowerCase() === correctAnswer)
  );

  const submit = async () => {
    setSubmitted(true);
    try {
      await api.post("/attempts", {
        lesson_id: lesson.lesson_id,
        answer: isMCQ ? picked : textAns,
        correct: (isMCQ && picked === c.answer) || (isInput && textAns.trim().toLowerCase() === correctAnswer),
        score: 1,
      });
      await api.put("/progress/update", {
        subject: lesson.subject,
        topic: lesson.topic,
        correct: (isMCQ && picked === c.answer) || (isInput && textAns.trim().toLowerCase() === correctAnswer),
      });
    } catch {}
  };

  return (
    <div className="min-h-screen bg-[#F7F9FC] pb-24" data-testid="lesson-screen">
      <header className="px-5 pt-6 max-w-xl mx-auto flex items-center gap-3">
        <button onClick={() => nav(-1)} data-testid="lesson-back" className="p-2 rounded-full bg-white border-2 border-gray-200">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <ProgressBar value={submitted ? 100 : 40} testId="lesson-progress" />
        </div>
        <div className="flex items-center gap-1 text-[#FF4B4B]">
          <Heart className="w-5 h-5 fill-current" />
          <span className="font-bold">5</span>
        </div>
      </header>

      <main className="max-w-xl mx-auto px-5 pt-8 flex flex-col gap-6">
        <div className="flex items-end gap-3">
          <Mashy mood={submitted ? (isCorrect ? "celebrate" : "confused") : "happy"} size={90} />
          <div className="card-chunky px-4 py-3 flex-1">
            <div className="text-xs text-gray-400 uppercase tracking-widest font-bold">{lesson.subject} • {lesson.topic}</div>
            <div className="font-display text-xl">{lesson.title}</div>
          </div>
        </div>

        <div className="card-chunky p-6">
          {c.latex && (
            <div className="mb-4 text-2xl"><BlockMath math={c.latex} /></div>
          )}
          {!c.latex && c.question && <div className="text-xl font-bold mb-2">{c.question}</div>}
          {c.prompt && <div className="text-xl font-bold mb-1">{c.prompt}</div>}
          {c.hindi_hint && <div className="text-sm text-gray-500">Hint: {c.hindi_hint}</div>}

          {isMCQ && (
            <div className="grid grid-cols-1 gap-3 mt-4">
              {(c.choices || []).map((opt, i) => {
                const sel = picked === opt;
                const showCorrect = submitted && opt === c.answer;
                const showWrong = submitted && sel && opt !== c.answer;
                return (
                  <motion.button
                    whileTap={{ scale: 0.97 }}
                    key={i}
                    disabled={submitted}
                    onClick={() => setPicked(opt)}
                    data-testid={`lesson-choice-${i}`}
                    className={`btn-chunky w-full justify-start text-left ${
                      showCorrect ? "bg-[#58CC02] border-[#46A302] text-white" :
                      showWrong ? "bg-[#FF9600] border-[#E58700] text-white" :
                      sel ? "bg-[#1CB0F6] border-[#1899D6] text-white" :
                      "bg-white border-gray-300 text-gray-700"
                    }`}
                  >
                    <span className="mr-3 font-display text-lg">{String.fromCharCode(65 + i)}</span>
                    {opt}
                  </motion.button>
                );
              })}
            </div>
          )}

          {isInput && (
            <input
              value={textAns}
              onChange={(e) => setTextAns(e.target.value)}
              disabled={submitted}
              data-testid="lesson-text-input"
              placeholder="Type your answer..."
              className="mt-4 w-full border-2 border-gray-300 rounded-2xl px-4 py-3 text-lg focus:border-[#1CB0F6] outline-none"
            />
          )}

          {isVoice && (
            <div className="mt-4 flex flex-col items-center gap-3">
              <button onClick={() => nav("/voice")} data-testid="lesson-go-voice" className="btn-primary">Open Voice Practice</button>
              <p className="text-sm text-gray-500">{c.expected && `Try saying: "${c.expected}"`}</p>
            </div>
          )}
        </div>

        <AnimatePresence>
          {submitted && (
            <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className={`card-chunky p-5 ${isCorrect ? "bg-[#E7F8D5] border-[#58CC02]" : "bg-[#FFF3DB] border-[#FF9600]"}`} data-testid="lesson-feedback">
              <div className="flex items-center gap-2 font-display text-xl">
                {isCorrect ? <Check className="w-6 h-6 text-[#58CC02]" /> : "💡"}
                {isCorrect ? "Sahi jawab! +10 XP" : "Try again — you're close!"}
              </div>
              <p className="text-sm text-gray-700 mt-1">
                {isCorrect ? "Mashy is super proud of you!" : `Correct answer: ${c.answer || c.expected}`}
              </p>
            </motion.div>
          )}
        </AnimatePresence>

        {!submitted ? (
          <button onClick={submit} disabled={(isMCQ && !picked) || (isInput && !textAns)} data-testid="lesson-submit"
            className="btn-success text-lg disabled:opacity-50 disabled:active:translate-y-0">
            Check Answer
          </button>
        ) : (
          <button onClick={() => nav("/dashboard")} data-testid="lesson-continue" className="btn-primary text-lg">
            Continue
          </button>
        )}
      </main>
    </div>
  );
}
